import React, { type ReactNode, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useLangStore } from '@app/dashboard/store/lang';
import * as amplitude from '@amplitude/analytics-browser';

import {
  BILLS_EVENTS,
  BILLS_EVENTS_PARAMS
} from '@app/dashboard/constants/tracking_events/bills';

import {
  CircleExclamation,
  Fire
} from 'libs/design-system/src/assets/Icons';
import {
  PageStatus,
  SkeletonLoader
} from '@unified-checkout/design-system';

import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { MOBILE_BREAKPOINT } from '@app/dashboard/constants/break-points';
import { useScreenWidth } from '@app/dashboard/hooks/useScreenWidth';
import { getSingleBillDetails } from '@app/dashboard/services/inner_layout/bills/get-single-bill-details';
import { MultiSectionCard } from '@app/dashboard/components/inner_layout/MultiSectionCard';
import { SummaryBox } from '@app/dashboard/components/inner_layout/Cards/SummaryBox';

import { Header } from './Header';

import styles from './single-bill.module.scss';

const EMPTY_VALUE = '-';

export const SingleBill = () => {
  const { t } = useTranslation();
  const { lang } = useLangStore();
  const screenWidth = useScreenWidth();
  const { id, type } = useParams<{ id: string; type: string }>();
  const isMobile = screenWidth <= MOBILE_BREAKPOINT;

  const {
    data,
    isLoading: isBillDetailsLoading,
    isError,
    isFetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_SINGLE_BILL_DETAILS, id, lang],
    queryFn: () => getSingleBillDetails(String(id), String(type)),
    enabled: !!id && !!type,
    refetchOnMount: 'always',
  });

  const responseData = data?.data?.data || data?.data;
  const paymentDetails = responseData?.details;
  const { trx_id, amount: billAmount, status: billStatusText } = paymentDetails || {};
  const { service_details } = data?.data?.data || {};

  useEffect(() => {
    if (!isBillDetailsLoading) {
      amplitude.track(BILLS_EVENTS.BILLS_BILL_DETAILS_PAGE_VIEWED, {
        [BILLS_EVENTS_PARAMS.BILL_CATEGORY]: service_details?.category_name,
        [BILLS_EVENTS_PARAMS.BILL_PROVIDER]: service_details?.service_name,
        [BILLS_EVENTS_PARAMS.BILL_SERVICE]: service_details?.service_name,
        [BILLS_EVENTS_PARAMS.TRANSACTION_ID]: trx_id,
        [BILLS_EVENTS_PARAMS.TOTAL_AMOUNT]: billAmount,
        [BILLS_EVENTS_PARAMS.BILL_STATUS]: billStatusText?.status_text,
        [BILLS_EVENTS_PARAMS.STATUS]: data?.data?.status,
      });
    }
  }, [isBillDetailsLoading]);

  const detailsBoxRenderer = ({
    box = undefined,
    height,
    noOfRows,
    key,
  }: {
    box: ReactNode;
    height: string;
    noOfRows: number;
    key: number | string;
  }): ReactNode => {
    return isBillDetailsLoading || isFetching ? (
      <SkeletonLoader
        type="bordered_box"
        noOfRows={noOfRows}
        height={height}
        key={key}
      />
    ) : (
      box && <React.Fragment key={key}>{box}</React.Fragment>
    );
  };

  const { payment_method, fees, net_amount, currency, commission } = paymentDetails || {};

  const headerData = {
    id: trx_id,
    amount: net_amount || 0,
    currency: currency || 'EGP',
    status: paymentDetails?.status?.original_status,
    date: paymentDetails?.created_at,
  };

  const billInfoBoxes = [
    detailsBoxRenderer({
      box: true && (
        <SummaryBox
          title={t('innerLayout.billsPage.singleBillPage.detailsBoxes.breakdown.title')}
          key="bill-details"
          itemsList={[
            {
              label: t('innerLayout.billsPage.singleBillPage.detailsBoxes.breakdown.transactionId'),
              value: trx_id || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.billsPage.singleBillPage.detailsBoxes.breakdown.paymentMethod'),
              value: payment_method || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.billsPage.singleBillPage.detailsBoxes.breakdown.billAmount'),
              value: currency + ' ' + billAmount || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.billsPage.singleBillPage.detailsBoxes.breakdown.fees'),
              value: currency + ' ' + fees?.fees_amount || EMPTY_VALUE,
            },
            {
              label: t('innerLayout.billsPage.singleBillPage.detailsBoxes.breakdown.netAmount'),
              value: currency + ' ' + net_amount || EMPTY_VALUE,
            },
            {
              label: {
                text: t('innerLayout.billsPage.singleBillPage.detailsBoxes.breakdown.commission'),
                leading_icon: Fire
              },
              value: currency + ' ' + commission || EMPTY_VALUE
            }
          ]}
        />
      ),
      noOfRows: 6,
      height: '20rem',
      key: 'bill-details',
    }),
    detailsBoxRenderer({
      box: true && (
        <MultiSectionCard
          key="saved-bill"
          title={service_details?.service_name}
          subtitle={service_details?.sub_title}
          icon={service_details?.service_icon}
        />
      ),
      noOfRows: 3,
      height: '10rem',
      key: 'saved-bill',
    }),
  ];

  const filteredBoxes = billInfoBoxes.filter(Boolean);

  const col1Boxes = filteredBoxes.filter((_, index) => index % 2 === 0);
  const col2Boxes = filteredBoxes.filter((_, index) => index % 2 !== 0);

  return (
    <div className={styles['single-bill']}>
      {isError ? (
        <PageStatus
          status="error"
          title={t('innerLayout.errors.smthWentWrong')}
          description={t('innerLayout.errors.dataRetrievalErr')}
          icon={CircleExclamation}
        />
      ) : (
        <>
          <Header
            data={headerData}
            isLoading={isBillDetailsLoading || isFetching}
            isError={isError}
          />

          <div className={styles['single-bill__main']}>
            {isMobile ? (
              <>
                {col1Boxes}
                {col2Boxes}
              </>
            ) : (
              <>
                <div className={styles['single-bill__main__col']}>
                  {col1Boxes}
                </div>
                <div className={styles['single-bill__main__col']}>
                  {col2Boxes}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};